<template>
  <div class="login-overlay" @click.self="onBackdrop">
    <div class="login-card">

      <div class="login-header">
        <h2>{{ t('changePassword') }}</h2>
        <button v-if="!forced" class="close-btn" @click="$emit('cancel')">&#10005;</button>
      </div>

      <div class="form">

        <div v-if="forced" class="info-message">{{ t('tempPasswordNotice') }}</div>

        <input
            v-if="!forced"
            v-model="oldPassword"
            type="password"
            :placeholder="t('currentPassword')"
        />

        <input
            v-model="newPassword"
            type="password"
            :placeholder="t('newPassword')"
            @keyup.enter="submit"
        />

        <input
            v-model="newPasswordRepeat"
            type="password"
            :placeholder="t('repeatNewPassword')"
            @keyup.enter="submit"
        />

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        <div v-if="successMessage" class="success-message">{{ successMessage }}</div>

        <button :disabled="!canSubmit" @click="submit">
          {{ t('changePassword') }}
        </button>

        <button class="secondary-btn" @click="onCancel">
          {{ forced ? t('logout') : t('cancel') }}
        </button>

      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "../i18n.js"
import ChatApiService from "../services/ChatApiService.js"

export default {
  name: "ChatChangePassword",

  props: {
    domain: { type: String, default: 'tibtix.com' },
    forced: { type: Boolean, default: false },
    initialOldPassword: { type: String, default: '' }
  },

  emits: ['changed', 'cancel', 'logout'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      oldPassword: this.initialOldPassword || '',
      newPassword: '',
      newPasswordRepeat: '',
      errorMessage: '',
      successMessage: ''
    }
  },

  computed: {
    canSubmit() {
      if (!this.newPassword || this.newPassword.length < 8) return false
      if (this.newPassword !== this.newPasswordRepeat) return false
      if (!this.forced && !this.oldPassword) return false
      return true
    }
  },

  methods: {
    async submit() {
      this.errorMessage = ''
      this.successMessage = ''
      if (this.newPassword.length < 8) {
        this.errorMessage = this.t('passwordTooShort')
        return
      }
      if (this.newPassword !== this.newPasswordRepeat) {
        this.errorMessage = this.t('passwordMismatch')
        return
      }
      try {
        await ChatApiService.changePassword(this.domain, this.oldPassword, this.newPassword)
        this.successMessage = this.t('passwordChanged')
        this.$emit('changed')
      } catch (e) {
        this.errorMessage = e.message || this.t('passwordChangeFailed')
      }
    },

    onCancel() {
      if (this.forced) this.$emit('logout')
      else this.$emit('cancel')
    },

    onBackdrop() {
      if (!this.forced) this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.login-header h2 { margin: 0; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.login-card {
  width: min(420px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

input {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
}

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}

button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}

.secondary-btn:hover { background: #e4e8ee; }

.info-message {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
